import { ApiDto } from '@portals/shared/portal/ApiDto';
import { useMemo, useState } from 'react';

import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ServiceDomainResponse } from '@/openapi/schema';
import { SERVICE_DOMAIN_KEY } from '@/pages/explorer/filters/ServiceDomainFilter';
import { STAGE_FILTER_KEY } from '@/pages/explorer/filters/StageFilter';
import { TAG_FILTER_KEY } from '@/pages/explorer/filters/TagFilter';
import { CLASSIFICATION_FILTER_KEY } from '@/pages/explorer/filters/TypeFilter';

// eslint-disable-next-line max-lines-per-function
export function useApiFilters(apis: ApiDto[] | undefined, serviceDomains: ServiceDomainResponse[] | undefined) {
  const { getSelectedKeysFromURL } = useUrlFilters();
  const [query, setQuery] = useState('');

  const serviceDomainIdsFromApi = useMemo(() => {
    if (!apis) return [];
    return [...new Set(apis.flatMap((api) => api.serviceNowServiceDomainId))];
  }, [apis]);

  const serviceDomainOptions = useMemo(() => {
    if (!serviceDomains) return [];
    return serviceDomains
      .map((serviceDomain) => ({
        content: [serviceDomain.serviceDomainId, serviceDomain.name],
        selectedKey: serviceDomain.id,
        selected_value: serviceDomain.name,
      }))
      .filter((api) => serviceDomainIdsFromApi.includes(api.selectedKey));
  }, [serviceDomainIdsFromApi, serviceDomains]);

  const serviceDomainFilter = useMemo(
    () => getSelectedKeysFromURL(SERVICE_DOMAIN_KEY, serviceDomainOptions),
    [getSelectedKeysFromURL, serviceDomainOptions],
  );

  const stageOptions = useMemo(() => {
    if (!apis) return [];
    const uniqueStages = Array.from(new Set(apis.map((api) => api.stage)));
    return uniqueStages.map((status) => ({
      selectedKey: status,
      content: [status],
    }));
  }, [apis]);

  const stageFilter = useMemo(
    () => getSelectedKeysFromURL(STAGE_FILTER_KEY, stageOptions),
    [getSelectedKeysFromURL, stageOptions],
  );

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    if (!apis) return [];
    set.add('all');
    apis.forEach(({ classification }) => set.add(classification));
    return Array.from(set);
  }, [apis]);

  const typeFilter = useMemo(
    () =>
      getSelectedKeysFromURL(
        CLASSIFICATION_FILTER_KEY,
        typeOptions.map((type) => ({
          selectedKey: type,
          content: [type],
        })),
      ),
    [getSelectedKeysFromURL, typeOptions],
  );

  const tagOptions = useMemo(() => {
    if (!apis) return [];
    const uniqueTags = [...new Set(apis.flatMap((api) => api.tags))];
    return uniqueTags.map((tag) => ({
      selectedKey: tag,
      content: [tag],
    }));
  }, [apis]);

  const tagFilter = useMemo(
    () => getSelectedKeysFromURL(TAG_FILTER_KEY, tagOptions),
    [getSelectedKeysFromURL, tagOptions],
  );

  const filteredApis = useMemo(() => {
    if (!apis) return [];

    const lowerCasedQuery = query.toLowerCase();

    return apis
      .filter((api) => stageFilter.length === 0 || stageFilter.includes(api.stage))
      .filter((api) => !lowerCasedQuery || api.name.toLowerCase().includes(lowerCasedQuery))
      .filter((api) => tagFilter.length === 0 || api.tags.some((tag) => tagFilter.includes(tag)))
      .filter((api) => typeFilter.length === 0 || typeFilter[0] === 'all' || typeFilter.includes(api.classification))
      .filter(
        (api) => serviceDomainFilter.length === 0 || serviceDomainFilter.includes(api.serviceNowServiceDomainId ?? ''),
      );
  }, [apis, query, tagFilter, typeFilter, serviceDomainFilter, stageFilter]);

  return {
    query,
    setQuery,
    tagOptions,
    tagFilter,
    typeOptions,
    filteredApis,
    serviceDomainFilter,
    serviceDomainOptions,
    stageFilter,
    stageOptions,
    typeFilter,
  };
}
